import { BookingBookingType, SlotPaymentMethod } from '@api/models';

import { BookingFormData } from './bookingSchema';

export interface BookingApiPayload {
  activityId: string;
  slotId: string;
  bookingType: BookingBookingType;
  bookingDate: string;
  startTime: number;
  endTime: number;
  numberOfPeople: number;
  addressLine1?: string;
  addressCity?: string;
  addressState?: string;
  addressZipCode?: string;
  addressCountry?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  notes?: string;
  paymentMethod: SlotPaymentMethod;
  /** Chosen location (physical/online). Omitted → server auto-resolves single-location slots. */
  locationId?: string;
}

/**
 * Transforms booking form data to API payload format
 * NOTE: Data should be validated using validateBookingForm() before calling this function
 */
export function transformBookingDataToApiPayload(formData: BookingFormData): BookingApiPayload {
  // formData.bookingDate is the occurrence's UTC slot_date (YYYY-MM-DD), set from
  // the selected time slot in usePickSlotLogic. It is the server's occurrence
  // identity (matched against slot_date) and must be sent verbatim — the date
  // pills are grouped by the viewer's local day, but that local day is never the
  // submitted value.
  const bookingDateString = formData.bookingDate;

  // Slot type already matches API format
  const bookingType = formData.bookingType as BookingBookingType;

  // Payment method must be explicitly set by the wizard for a paid slot.
  // It should be set when selecting a time slot based on the slot's accepted payment methods.
  // A free slot (basePrice 0) has none to choose from (see BookingStepsFlow's
  // getPaymentMethodFromSlot, which returns no paymentMethod when the slot accepts
  // neither), so fall back to a placeholder - the backend ignores the slot's
  // accepted-methods check once the slot's price is 0.
  const isFreeSlot = (formData.basePrice ?? 0) <= 0;
  if (!formData.paymentMethod && !isFreeSlot) {
    throw new Error('Payment method is required. Please select a time slot first.');
  }
  const paymentMethod = (formData.paymentMethod ?? 'online') as SlotPaymentMethod;

  const payload: BookingApiPayload = {
    activityId: formData.activityId || '',
    slotId: formData.slotId || '',
    bookingType,
    bookingDate: bookingDateString,
    startTime: formData.timeSlot!.startTime,
    endTime: formData.timeSlot!.endTime,
    numberOfPeople: formData.bookingType === 'GROUP' ? formData.numberOfPeople || 1 : 1,
    paymentMethod,
    ...(formData.locationId ? { locationId: formData.locationId } : {})
  };

  // Add address if provided (flat fields)
  if (formData.address) {
    payload.addressLine1 = formData.address.addressLine1;
    payload.addressCity = formData.address.city;
    payload.addressState = formData.address.state;
    payload.addressZipCode = formData.address.zipCode;
    payload.addressCountry = formData.address.country;
    payload.addressLatitude = formData.address.latitude;
    payload.addressLongitude = formData.address.longitude;
  }

  // Add notes if provided
  if (formData.notes && formData.notes.trim()) {
    payload.notes = formData.notes.trim();
  }

  return payload;
}
